import { HTMLProps, useEffect, useRef } from 'react'

import { animate, createTimeline, morphTo } from 'animejs'

export function IconPWLogo ({
    className,
    ...props
}: HTMLProps<SVGSVGElement>)
{
    const animating = useRef<boolean>(true)

    const root = useRef<SVGSVGElement>(null)

    const leftEye = useRef<SVGGElement>(null)
    const rightEye = useRef<SVGGElement>(null)
    const watchText = useRef<SVGGElement>(null)

    const leftEyeBG = useRef<SVGPathElement>(null)
    const rightEyeBG = useRef<SVGPathElement>(null)

    const leftEyeball = useRef<SVGCircleElement>(null)
    const rightEyeball = useRef<SVGCircleElement>(null)

    const eyeBGClose = useRef<SVGPathElement>(null)
    const eyeBGOpen = useRef<SVGPathElement>(null)

    const toggleEyeballs = (close: boolean) => {
        if (animating.current) {
            return
        }

        const leftEyeBGEl = leftEyeBG.current
        const rightEyeBGEl = rightEyeBG.current

        const leftEyeballEl = leftEyeball.current
        const rightEyeballEl = rightEyeball.current

        const openEl = eyeBGOpen.current
        const closeEl = eyeBGClose.current
        if (!leftEyeBGEl || !rightEyeBGEl || !leftEyeballEl || !rightEyeballEl || !openEl || !closeEl) {
            return
        }

        animate([leftEyeBGEl, rightEyeBGEl], {
            d: morphTo(close ? closeEl : openEl),
            duration: 125,
            ease: close ? 'in' : 'out',
        })

        animate([leftEyeBGEl, rightEyeBGEl], {
            fill: close ? '#00000000' : '#000000',
            stroke: close ? '#000000' : '#00000000',
            duration: 125,
            ease: 'linear',
        })

        animate([rightEyeballEl, leftEyeballEl], {
            opacity: close ? 0 : 1,
            duration: 125,
            ease: close? 'in' : 'out'
        })
    }

    const moveEyeballs = (e: PointerEvent) => {
        if (animating.current) {
            return
        }

        const leftEl = leftEyeball.current
        const rightEl = rightEyeball.current
        if (!leftEl || !rightEl) {
            return
        }

        const leftBox = leftEl.getBoundingClientRect()
        const { left: leftLeft, top: leftTop, width: leftWidth, height: leftHeight } = leftBox
        const leftX = leftLeft + leftWidth / 2
        const leftY = leftTop + leftHeight / 2

        const rightBox = rightEl.getBoundingClientRect()
        const { left: rightLeft, top: rightTop, width: rightWidth, height: rightHeight } = rightBox
        const rightX = rightLeft + rightWidth / 2
        const rightY = rightTop + rightHeight / 2

        const { clientX, clientY } = e

        const leftDX = Math.max(Math.min(clientX - leftX, 25), -25);
        const leftDY = Math.max(Math.min(clientY - leftY, 25), -25);

        const rightDX = Math.max(Math.min(clientX - rightX, 25), -25);
        const rightDY = Math.max(Math.min(clientY - rightY, 25), -25);

        animate(leftEl, {
            x: leftDX,
            y: leftDY,
            duration: `${Math.sqrt(leftDX*leftDX + leftDY*leftDY)/40*100}ms`,
            ease: 'linear',
        })

        animate(rightEl, {
            x: rightDX,
            y: rightDY,
            duration: `${Math.sqrt(rightDX*rightDX + rightDY*rightDY)/40*100}ms`,
            ease: 'linear',
        })
    }

    useEffect(() =>
    {
        const clear = () => {
            document.removeEventListener('pointermove', (e) => {
                moveEyeballs(e)
            })
        }

        document.addEventListener('pointermove', (e) => {
            moveEyeballs(e)
        })

        return () => {
            clear()
        }
    }, [])

    useEffect(() =>
    {
        const el = root.current
        const leftEyeEl = leftEye.current
        const rightEyeEl = rightEye.current
        const watchTextEl = watchText.current
        const leftEyeBGEl = leftEyeBG.current
        const rightEyeBGEl = rightEyeBG.current
        const eyeBGCloseEl = eyeBGClose.current
        const eyeBGOpenEl = eyeBGOpen.current
        const leftEyeballEl = leftEyeball.current
        const rightEyeballEl = rightEyeball.current

        if (!el || !leftEyeEl || !rightEyeEl || !watchTextEl || !leftEyeBGEl || !rightEyeBGEl || !eyeBGCloseEl || !eyeBGOpenEl || !leftEyeballEl || !rightEyeballEl) {
            return
        }

        const tl = createTimeline({
            loop: true,
            loopDelay: 5_000,
            delay: 5_000,
            alternate: false,

            onBegin: () => {
                animating.current = true
            }
        })

        tl.label('hideText')
            .add(watchTextEl, {
                opacity: 0,
                duration: 125,
                ease: 'out',
            }, 'hideText')
            .add([leftEyeEl, rightEyeEl], {
                opacity: 1,
                duration: 125,
                ease: 'out',
            }, 'hideText')
            .label('openEyes0', '<')
            .add([leftEyeBGEl, rightEyeBGEl], {
                d: morphTo(eyeBGOpenEl as any),
                stroke: '#00000000',
                fill: '#000000',
                duration: 250,
                ease: 'out',
            }, 'openEyes0')
            .add([leftEyeballEl, rightEyeballEl], {
                opacity: 1,
                duration: 250,
                ease: 'out',
            }, 'openEyes0')
            .label('moveEyeballs0', '<+=1000')
            .add([leftEyeballEl, rightEyeballEl], {
                x: 20,
                y: 20,
                duration: 333,
                ease: 'out',
            }, 'moveEyeballs0')
            .label('moveEyeballs1', '<+=1000')
            .add([leftEyeballEl, rightEyeballEl], {
                x: -40,
                y: -10,
                duration: 333,
                ease: 'out',
            }, 'moveEyeballs1')
            .label('moveEyeballs2', '<+=1000')
            .add([leftEyeballEl, rightEyeballEl], {
                x: 0,
                y: 0,
                duration: 333,
                ease: 'out',
            }, 'moveEyeballs2')
            .label('closeEyes1', '<+=1000')
            .add([leftEyeBGEl, rightEyeBGEl], {
                d: morphTo(eyeBGCloseEl),
                stroke: '#000000',
                fill: '#00000000',
                duration: 125,
                ease: 'in',
            }, 'closeEyes1')
            .add([leftEyeballEl, rightEyeballEl], {
                opacity: 1,
                duration: 125,
                ease: 'in',
            }, 'closeEyes1')
            .label('openEyes1', '<')
            .add([leftEyeBGEl, rightEyeBGEl], {
                d: morphTo(eyeBGOpenEl as any),
                stroke: '#00000000',
                fill: '#000000',
                duration: 250,
                ease: 'out',
            }, 'openEyes1')
            .add([leftEyeballEl, rightEyeballEl], {
                opacity: 1,
                duration: 250,
                ease: 'in',
                onComplete: ()=>
                {
                    animating.current = false
                    el.style.pointerEvents = 'auto'
                },
            }, 'openEyes1')
            .label('closeEyes2', '<+=20000')
            .add([leftEyeBGEl, rightEyeBGEl], {
                d: morphTo(eyeBGCloseEl),
                stroke: '#000000',
                fill: '#00000000',
                duration: 125,
                ease: 'in',
                onBegin: () =>
                {
                    animating.current = true
                    el.style.pointerEvents = 'none'
                },
            }, 'closeEyes2')
            .add([leftEyeballEl, rightEyeballEl], {
                opacity: 1,
                duration: 125,
                ease: 'in',
            }, 'closeEyes2')
            .label('showText2', '<+=500')
            .add([leftEyeEl, rightEyeEl], {
                opacity: 0,
                duration: 125,
                ease: 'out',
            }, 'showText2')
            .add(watchTextEl, {
                opacity: 1,
                duration: 125,
                ease: 'out',
            }, 'showText2')

        return () =>
        {
            tl.cancel()
        }
    }, [root, leftEye, rightEye, watchText, leftEyeBG, rightEyeBG, eyeBGClose, eyeBGOpen, leftEyeball, rightEyeball])

    return (
        <svg
            className={className}
            viewBox="0 0 1086 188"
            fill="none"
            xmlns="http://www.w3.org/2000/svg" {...props}
            onPointerEnter={() => toggleEyeballs(true)}
            onPointerLeave={() => toggleEyeballs(false)}
            ref={root}
        >
            <path
                d="M525 7C525 40.1371 551.863 67 585 67H594C627.137 67 654 40.1371 654 7V1L1086 0V188H654V179C654 145.863 627.137 119 594 119H585C551.863 119 525 145.863 525 179V187L0 188V0H525V7Z"
                fill="#12F71A"
            />
            <path
                d="M47.2578 160.11V66.876H65.5267L66.5346 74.5615C70.0624 68.7659 77.9999 64.8601 86.9453 64.8601C104.458 64.8601 116.679 77.0814 116.679 97.3661C116.679 117.273 105.844 131.636 87.0713 131.636C78.3779 131.636 70.3144 128.36 66.6606 123.572V160.11H47.2578ZM66.7866 98.374C66.7866 107.823 73.0862 114.123 82.1576 114.123C91.3551 114.123 97.1507 107.697 97.1507 98.374C97.1507 89.0506 91.3551 82.625 82.1576 82.625C73.0862 82.625 66.7866 88.9246 66.7866 98.374Z"
                fill="#000D04"
            />
            <path
                d="M171.19 66.876V85.1449H165.016C153.929 85.1449 147.251 90.0586 147.251 102.532V129.872H127.848V67.002H146.117L147.125 76.3254C149.771 70.1518 155.315 65.9941 163.756 65.9941C166.024 65.9941 168.544 66.2461 171.19 66.876Z"
                fill="#000D04"
            />
            <path
                d="M190.577 57.0486C184.403 57.0486 179.489 52.1349 179.489 45.9613C179.489 39.7877 184.403 35 190.577 35C196.624 35 201.538 39.7877 201.538 45.9613C201.538 52.1349 196.624 57.0486 190.577 57.0486ZM181.001 129.872V66.876H200.404V129.872H181.001Z"
                fill="#000D04"
            />
            <path
                d="M232.851 129.872L208.283 66.876H229.197L236.883 88.9246C240.033 98.1221 242.553 106.06 243.056 108.957C243.812 105.304 246.584 97.1141 249.734 88.9246L258.176 66.876H278.334L252.002 129.872H232.851Z"
                fill="#000D04"
            />
            <path
                d="M302.149 131.51C288.794 131.51 280.478 123.698 280.478 111.351C280.478 99.7599 288.668 92.5784 303.787 91.4445L321.426 90.0586V89.0506C321.426 82.877 317.646 79.6012 310.716 79.6012C302.527 79.6012 298.117 82.751 298.117 88.4207H281.99C281.99 74.1835 293.707 64.8601 311.724 64.8601C329.993 64.8601 340.324 75.0655 340.324 93.0824V129.872H323.189L321.93 121.557C319.914 127.352 311.598 131.51 302.149 131.51ZM308.952 117.147C316.386 117.147 321.552 113.493 321.552 106.312V102.91L311.724 103.792C303.283 104.548 300.259 106.438 300.259 110.469C300.259 115.005 303.031 117.147 308.952 117.147Z"
                fill="#000D04"
            />
            <path
                d="M350.839 98.1221C350.839 78.7193 364.194 64.8601 383.093 64.8601C401.74 64.8601 414.087 75.0655 415.347 91.5705H395.818C394.432 85.2709 390.526 82.247 383.849 82.247C375.659 82.247 370.368 88.4207 370.368 98.1221C370.368 108.201 375.281 114.123 383.597 114.123C390.4 114.123 394.558 110.973 395.818 104.8H415.347C414.087 120.675 401.236 131.51 383.597 131.51C363.69 131.51 350.839 118.281 350.839 98.1221Z"
                fill="#000D04"
            />
            <path
                d="M419.702 160.11V143.857H430.412C436.963 143.857 439.735 141.841 442.255 135.038L443.389 132.014L418.064 66.876H438.853L452.586 107.319L467.831 66.876H487.99L455.988 143.605C450.696 156.456 443.641 161.874 432.554 161.874C427.892 161.874 423.482 161.244 419.702 160.11Z"
                fill="#000D04"
            />

            <g ref={watchText} opacity={1}>
                <path
                    d="M711.804 129.998L691.52 67.002H711.93L717.726 87.5387C719.238 92.9564 720.624 99.13 721.632 105.43C722.766 98.752 723.396 96.3582 726.167 87.5387L732.593 67.002H752.5L758.421 87.5387C759.051 89.6806 762.075 101.398 762.705 105.43C763.461 100.894 766.485 89.8066 767.115 87.5387L773.162 67.002H793.699L771.651 129.998H753.634L747.586 109.965C744.436 99.634 742.798 92.2004 742.42 89.5546C741.916 92.2004 740.782 97.1141 736.751 110.343L730.703 129.998H711.804Z"
                    fill="#000D04"
                />
                <path
                    d="M818.099 131.636C804.744 131.636 796.429 123.824 796.429 111.477C796.429 99.8859 804.618 92.7044 819.737 91.5705L837.376 90.1845V89.1766C837.376 83.003 833.596 79.7272 826.667 79.7272C818.477 79.7272 814.068 82.877 814.068 88.5466H797.941C797.941 74.3095 809.658 64.9861 827.675 64.9861C845.944 64.9861 856.275 75.1915 856.275 93.2084V129.998H839.14L837.88 121.683C835.864 127.478 827.549 131.636 818.099 131.636ZM824.903 117.273C832.336 117.273 837.502 113.619 837.502 106.438V103.036L827.675 103.918C819.233 104.674 816.209 106.564 816.209 110.595C816.209 115.131 818.981 117.273 824.903 117.273Z"
                    fill="#000D04"
                />
                <path
                    d="M895.026 129.998H875.624V83.129H863.654V67.002H875.624V47.4732H895.026V67.002H906.996V83.129H895.026V129.998Z"
                    fill="#000D04"
                />
                <path
                    d="M911.699 98.248C911.699 78.8453 925.054 64.9861 943.953 64.9861C962.599 64.9861 974.947 75.1915 976.207 91.6965H956.678C955.292 85.3969 951.386 82.373 944.709 82.373C936.519 82.373 931.227 88.5466 931.227 98.248C931.227 108.327 936.141 114.249 944.457 114.249C951.26 114.249 955.418 111.099 956.678 104.926H976.207C974.947 120.801 962.096 131.636 944.457 131.636C924.55 131.636 911.699 118.407 911.699 98.248Z"
                    fill="#000D04"
                />
                <path
                    d="M1006.24 129.998H986.832V35H1006.36V73.5536C1010.27 68.1359 1017.45 64.9861 1025.64 64.9861C1040.63 64.9861 1049.32 74.5615 1049.32 91.0665V129.998H1029.92V95.7282C1029.92 87.7907 1025.51 82.625 1018.83 82.625C1011.15 82.625 1006.24 87.6647 1006.24 95.4762V129.998Z"
                    fill="#000D04"
                />
            </g>

            <g opacity={0}>
                <path
                    d={'M2.69531 40.1816L24.4511 60.0193C53.3707 86.3892 96.6993 86.955 126.26 61.3488L150.695 40.1816'}
                    fill={'#00000000'}
                    stroke={'#000000'}
                    strokeWidth={8}
                    ref={eyeBGClose}
                />

                <path
                    ref={eyeBGOpen}
                    d={'M0 40.0001L21.7558 20.1624C50.6755 -6.20762 94.004 -6.77353 123.564 18.8329L148 40.0001L123.564 61.1672C94.004 86.7734 50.6754 86.2076 21.7558 59.8377L0 40.0001Z'}
                    fill={'transparent'}
                    stroke={'transparent'}
                />
            </g>

            <g transform={`translate(697 54)`} ref={leftEye} opacity={0}>

                <path
                    d={'M2.69531 40.1816L24.4511 60.0193C53.3707 86.3892 96.6993 86.955 126.26 61.3488L150.695 40.1816'}
                    fill={'#00000000'}
                    stroke={'#000000'}
                    strokeWidth={8}
                    ref={leftEyeBG}
                />

                <circle cx={74} cy={40} r={21.8105} fill="#12F71A" ref={leftEyeball} opacity={0} />

            </g>

            <g transform={`translate(901 54)`} ref={rightEye} opacity={0}>

                <path
                    d={'M2.69531 40.1816L24.4511 60.0193C53.3707 86.3892 96.6993 86.955 126.26 61.3488L150.695 40.1816'}
                    fill={'#00000000'}
                    stroke={'#000000'}
                    strokeWidth={8}
                    ref={rightEyeBG}
                />

                <circle cx={74} cy={40} r={21.8105} fill="#12F71A" ref={rightEyeball} opacity={0} />

            </g>
        </svg>
    )
}